import type { MarketingMobileNumber } from '@freelancer/users-shared';
import { isDefined } from '@freelancer/utils';
import type { AddressApi } from 'api-typings/common/common';
import type {
  DateOfBirthApi,
  DirectoryAndFollowPreferencesApi,
  MarketingMobileNumberApi,
  MarketingMobileNumberResultApi,
  SecurePhoneGetResultApi,
  UserApi,
} from 'api-typings/users/users';
import { getDate, getMonth, getYear } from 'date-fns';
import { transformCurrency } from '../currencies/currencies.transformers';
import { transformEquipmentGroup } from '../equipment-group/equipment-group.transformers';
import { transformOperatingArea } from '../operating-areas/operating-areas.transformers';
import type { SecurePhone } from '../security-phone-requests/security-phone-requests.model';
import { transformUserStatus } from '../users/users.transformers';
import type {
  DirectoryAndFollowPreferences,
  UsersSelf,
} from './users-self.model';

/**
 * This transforms a usersSelf GET request with the projections set.
 */
export function transformUsersSelf(user: UserApi): UsersSelf {
  if (!user.status) {
    throw new ReferenceError('User missing field `status`');
  }

  if (!isDefined(user.limited_account)) {
    throw new ReferenceError('User missing field `limited_account`');
  }

  return {
    ...transformUsersSelfWithoutProjections(user),
    status: transformUserStatus(user.status),
    marketingMobileNumber: user.marketing_mobile_number
      ? transformMarketingMobileNumber(user.marketing_mobile_number)
      : undefined,
    isLimitedAccount: user.limited_account,
    securePhone: user.secure_phone
      ? transformSecurePhone(user.secure_phone)
      : undefined,
    isNominatedPaymentControlled: user.is_nominated_payment_controlled,
    operatingAreas: user.operating_areas
      ? user.operating_areas.map(operatingAreaApi =>
          transformOperatingArea(operatingAreaApi),
        )
      : undefined,
    equipmentGroups: user.equipment_groups
      ? user.equipment_groups.map(equipmentGroup =>
          transformEquipmentGroup(equipmentGroup),
        )
      : undefined,
  };
}

/**
 * This transforms a usersSelf update request WITHOUT any projections set.
 */
function transformUsersSelfWithoutProjections(
  user: UserApi,
): Omit<UsersSelf, 'status' | 'marketingMobileNumber' | 'isLimitedAccount'> {
  if (!user.primary_currency) {
    throw new ReferenceError('User without primary currency!');
  }

  if (!user.registration_date) {
    throw new ReferenceError('User missing field `registration_date`');
  }

  return {
    id: user.id,
    username: user.username,
    firstName: user.first_name,
    lastName: user.last_name,
    publicName: user.public_name,
    displayName: user.display_name,
    primaryCurrency: transformCurrency(user.primary_currency),
    address: user.address,
    dateOfBirth: user.date_of_birth,
    primaryLanguage: user.primary_language,
    hasLinkedEscrowComAccount: user.escrowcom_account_linked
      ? user.escrowcom_account_linked
      : false,
    email: user.email,
    ...getUsersSelfComputedFields(
      user.first_name,
      user.last_name,
      user.address,
      user.date_of_birth,
    ),
    enterpriseIds: user.enterprise_ids,
    freelancerVerifiedStatus: user.freelancer_verified_status,
    chosenRole: user.chosen_role,
    directoryAndFollowPreferences: transformDirectoryFollowPrefs(
      user.directory_follow_preferences,
    ),
    registrationDate: user.registration_date * 1000,
  };
}

/**
 * This transforms a usersSelf update request WITHOUT any projections set.
 */
export function mergeUpdateSuccessResult(
  user: UserApi,
  originalUser: UsersSelf,
): UsersSelf {
  return {
    status: originalUser.status,
    marketingMobileNumber: originalUser.marketingMobileNumber,
    isLimitedAccount: originalUser.isLimitedAccount,
    ...transformUsersSelfWithoutProjections(user),
  };
}

/**
 * These fields aren't returned by the backend but are computed from other
 * fields on `UsersSelf`
 */
export function getUsersSelfComputedFields(
  firstName?: string,
  lastName?: string,
  address?: AddressApi,
  dob?: DateOfBirthApi,
): Pick<UsersSelf, 'profileCompleted' | 'escrowProfileCompleted'> {
  return {
    profileCompleted: !!firstName && !!lastName && !!address,
    escrowProfileCompleted:
      !!firstName &&
      !!lastName &&
      !!dob &&
      !!address &&
      !!address.address1 &&
      !!address.city &&
      !!address.state_code &&
      !!address.country &&
      !!address.zip,
  };
}

export function transformDOBToDMY(dob: Date): DateOfBirthApi {
  return {
    day: getDate(dob),
    month: getMonth(dob) + 1,
    year: getYear(dob),
  };
}

/** The format of fl-input uses for date is ISO String */
export function transformDMYToDOBString(dob: DateOfBirthApi): string {
  const date = new Date(dob.year, dob.month - 1, dob.day);
  return date.toISOString();
}

export function transformMarketingMobileNumberAPIResult(
  marketingMobileNumberResult: MarketingMobileNumberResultApi,
  original: UsersSelf,
): UsersSelf {
  const marketingMobileNumber = {
    phoneNumber:
      marketingMobileNumberResult.marketing_mobile_number.phone_number,
    countryCode:
      marketingMobileNumberResult.marketing_mobile_number.country_code,
    status: marketingMobileNumberResult.marketing_mobile_number.status,
  };
  return {
    ...original,
    marketingMobileNumber,
  };
}

function transformMarketingMobileNumber(
  marketingMobileNumber: MarketingMobileNumberApi,
): MarketingMobileNumber {
  return {
    phoneNumber: marketingMobileNumber.phone_number,
    countryCode: marketingMobileNumber.country_code,
    status: marketingMobileNumber.status,
  };
}

export function transformSecurePhone(
  securePhone: SecurePhoneGetResultApi,
): SecurePhone {
  return {
    phoneNumber: securePhone.phone_number,
    countryCode: securePhone.country_code,
    country: securePhone.country,
  };
}

function transformDirectoryFollowPrefs(
  preferences?: DirectoryAndFollowPreferencesApi,
): DirectoryAndFollowPreferences {
  return {
    allowListing: preferences?.allow_listing,
    allowWatch: preferences?.allow_watch,
    allowSealPriceOnReview: preferences?.allow_seal_price_on_review,
  };
}
